import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../program-sheet.module.scss';
import clsx from 'clsx';
import { Area } from '@norlys-a-s/norlys-play-kmm';

import {
  doesFocusableExist,
  getCurrentFocusKey,
  setFocus
} from '@noriginmedia/norigin-spatial-navigation';
import { focusKeyPrevAtom, isErrorDialogOpenAtom, pageAreaAtom } from '@smart-tv/atoms';
import { Dialog, DialogContent } from '@smart-tv/components/dialog';
import testIds from '@smart-tv/e2e/shared/variables';
import useCollections from '@smart-tv/hooks/use-collections';
import useDialog from '@smart-tv/hooks/use-dialog';
import useQuery from '@smart-tv/hooks/use-query';
import useNPVRSheet from '@smart-tv/hooks/use-npvr';
import { ContentType, GetContentInfoParams } from '@smart-tv/typings/content';
import decodeProgramSheetParams from '@smart-tv/utils/routing/decode-program-sheet-params';
import removeQueryParams from '@smart-tv/utils/routing/remove-query-params';

import { avoidDefaultErrorRedirectAtom } from '../../error-dialog/error-dialog';
import { usePlayerContext } from '../../player/_contexts/player-context';
import { rootStore } from '../_stores/store-root';
import { uiStore } from '../_stores/store-ui';
import useSheetStore from '../_stores/useSheetStore';
import { handleFocusKeyPrev } from '../_utils/focusKeyUtils';
import ProgramSheetBackground from './sheet-background';
import ProgramSheetContent from './sheet-content';
import SheetDescriptionExtended from './sheet-description-extended';
import ProgramSheetHeader from './sheet-header';
import SheetProviders from './sheet-providers';
import RecordingDialog from '../../dialog/recording-dialog';
import { FocusKeysRecordingDialog, FocusKeysSheet } from '../sheet-helpers';
import { useNetwork } from '@smart-tv/hooks/use-network/use-network';
import NetworkErrorDialog from '../../error-dialog/network-error-dialog';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { toContentListProviders } from '@smart-tv/utils/kmm/helpers';
import ProgramSheetSkeleton from './program-sheet-skeleton';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import { isSheetDialogOpen } from '../_utils/focusKeyUtils';

export interface QueryParams {
  programsheet: string;
}

export const ProgramSheet = observer(() => {
  const navigate = useNavigate();
  const { player } = usePlayerContext();
  const setAvoidDefaultErrorRedirect = useSetAtom(avoidDefaultErrorRedirectAtom);
  const [focusKeyPrev, setFocusKeyPrev] = useAtom(focusKeyPrevAtom);
  const { programsheet } = useQuery<QueryParams>();
  const { openCollections } = useCollections();
  const { handleSetDialogFocusKey } = useDialog();
  const pageAreaAtomValue = useAtomValue(pageAreaAtom);
  const errorOpen = useAtomValue(isErrorDialogOpenAtom);
  const { isOffline, setIsOffline, isNetworkDialogOpen, checkAndUpdateNetwork } = useNetwork();
  const [showSheet, setShowSheet] = useState(true);
  const wasSheetHiddenByPlayerRef = useRef(false);
  const handleCloseRef = useRef<() => void>();
  const isOfflineRef = useRef(isOffline);

  useEffect(() => {
    rootStore.sheetStore.setCheckNetworkCallback(checkAndUpdateNetwork);
  }, [checkAndUpdateNetwork]);

  // Restore focus to sheet when player closes
  useLayoutEffect(() => {
    if (player) {
      wasSheetHiddenByPlayerRef.current = true;
      uiStore.toggleFocus(false);
    } else if (wasSheetHiddenByPlayerRef.current) {
      wasSheetHiddenByPlayerRef.current = false;
      // Restore focus to the primary action button when sheet becomes visible again
      const primaryButton = rootStore.sheetStore.sheet?.buttons?.primary;
      if (isSheetDialogOpen() || errorOpen) return;
      if (primaryButton) {
        setFocus(FocusKeysSheet.FOCUS_KEY_WATCH_LIVE);
      } else {
        setFocus(FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION);
      }
    }
  }, [player, errorOpen]);

  const {
    sid = 0,
    eventId = 0,
    contentId = 0,
    contentType = typeof ContentType,
    subProviderId = undefined,
    universeId = undefined,
    provider = undefined
  } = decodeProgramSheetParams(programsheet);

  const handleClose = useCallback(() => {
    setIsOffline(false);
    handleFocusKeyPrev(focusKeyPrev, openCollections, handleSetDialogFocusKey, setFocusKeyPrev);
    navigate(removeQueryParams(['programsheet']));
    rootStore.sheetStore.cancelScheduledRefresh();
  }, [
    setIsOffline,
    focusKeyPrev,
    openCollections,
    handleSetDialogFocusKey,
    setFocusKeyPrev,
    navigate
  ]);

  // Keep refs updated for use in escape handler (avoids stale closure in useLayoutEffect)
  handleCloseRef.current = handleClose;
  isOfflineRef.current = isOffline;

  const { recordingDialogContentProps, closeDialogForRecording, resetNPVRSheet } = useNPVRSheet({
    onClose: handleClose
  });

  if (sid && eventId) {
    const ProgramParams: GetContentInfoParams = {
      sid,
      eventId,
      contentType: ContentType.Program,
      fetchReady: true,
      universeId: universeId ? parseInt(universeId) : undefined,
      subProviderId,
      providers: toContentListProviders(provider)
    };

    useSheetStore(ProgramParams);
  } else if (contentId) {
    const VodParams: GetContentInfoParams = {
      contentId,
      contentType: contentType as ContentType,
      fetchReady: true,
      providerCollectionId:
        pageAreaAtomValue instanceof Area.ProviderCollectionArea
          ? pageAreaAtomValue.providerCollectionId
          : undefined,
      universeId: universeId ? parseInt(universeId) : undefined,
      subProviderId,
      providers: toContentListProviders(provider)
    };
    useSheetStore(VodParams);
  } else {
    setAvoidDefaultErrorRedirect(true);
  }

  const isSheetSettled =
    !rootStore.sheetStore.loading &&
    !rootStore.sheetStore.isRefreshPending &&
    !rootStore.episodesStore.isFetchingEpisodes;

  // A slow sheet refresh after the player closes can move focus away from the recording dialog.
  useEffect(() => {
    if (!recordingDialogContentProps || !isSheetSettled || player) return;

    const currentFocusKey = getCurrentFocusKey();
    const isFocusInDialog = Object.values(FocusKeysRecordingDialog).some(
      (key) => key === currentFocusKey
    );

    if (!isFocusInDialog && doesFocusableExist(FocusKeysRecordingDialog.SINGLE)) {
      setFocus(FocusKeysRecordingDialog.SINGLE);
    }
  }, [recordingDialogContentProps, isSheetSettled, player]);

  useEffect(() => {
    checkAndUpdateNetwork();
  }, [checkAndUpdateNetwork]);

  useEffect(() => {
    return () => {
      uiStore.reset();
      resetNPVRSheet();
    };
  }, []);

  useEffect(() => {
    if (rootStore.sheetStore.sheet && !rootStore.sheetStore.sheet.buttons) {
      setFocus(FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION);
    }
  }, []);

  useLayoutEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      // Don't handle ESC when sheet is hidden by player or network error dialog is showing
      if (wasSheetHiddenByPlayerRef.current || isOfflineRef.current) {
        return;
      }

      if (
        isBackKey(event) &&
        !uiStore.toggles.providersVersionsDialog &&
        !uiStore.toggles.recordingDialog
      ) {
        const availableActionButton = Object.values(FocusKeysSheet).find((key) => {
          const exists = doesFocusableExist(key);

          if (exists) {
            return key;
          }
        });

        // When focus is in episodes, move back to action buttons (Play button)
        if (
          uiStore.toggles.focusEpisodes &&
          !uiStore.toggles.extendedEpisodeDescriptionDialog.toggle
        ) {
          event.stopPropagation();
          uiStore.toggleFocus(false);
          availableActionButton
            ? setFocus(availableActionButton)
            : setFocus(FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION);
          return;
        }

        // When not in episode focus mode and no special dialogs are open, close the sheet
        // This handles ESC on action buttons (Play, etc.) after exiting episode focus mode
        if (
          !uiStore.toggles.focusEpisodes &&
          !uiStore.toggles.extendedEpisodeDescriptionDialog.toggle
        ) {
          event.stopPropagation();
          handleCloseRef.current?.();
          return;
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleNetworkRestored = useCallback(() => {
    setShowSheet(false);
    setTimeout(() => {
      setIsOffline(false);
      setShowSheet(true);
    }, 300);
  }, [setIsOffline]);

  const handleTryAgain = useCallback(async () => {
    rootStore.sheetStore.fetchSheet();
  }, []);

  if (isOffline && !isNetworkDialogOpen)
    return (
      <NetworkErrorDialog
        onNetworkRestored={handleNetworkRestored}
        onTryAgain={handleTryAgain}
        onClose={handleClose}
        enableFadeOut={true}
      />
    );
  if (!programsheet || !showSheet) return null;

  // Hide sheet while player is active instead of unmounting to preserve state
  const isHiddenByPlayer = !!player;

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      isBlocking={!isHiddenByPlayer}
      showCloseButton={true}
      disableEscapeKeyDown={
        uiStore.toggles.extendedEpisodeDescriptionDialog.toggle ||
        uiStore.toggles.providersVersionsDialog ||
        uiStore.toggles.recordingDialog ||
        uiStore.toggles.focusEpisodes ||
        isHiddenByPlayer
      }
      dialogContainerProps={{
        'data-testid': testIds.programsheet.dialog,
        style: isHiddenByPlayer ? { visibility: 'hidden' } : undefined
      }}
    >
      <DialogContent>
        {rootStore.sheetStore.loading ? (
          <ProgramSheetSkeleton />
        ) : (
          <>
            {!uiStore.toggles.providersVersionsDialog &&
              !uiStore.toggles.extendedEpisodeDescriptionDialog.toggle && (
                <>
                  <ProgramSheetBackground />
                  <div
                    className={clsx(styles['program-sheet'], {
                      [styles['program-sheet--focused']]: uiStore.toggles.focusEpisodes,
                      [styles['program-sheet--scrollable']]: IS_BROWSER
                    })}
                  >
                    <ProgramSheetHeader />
                    <ProgramSheetContent />
                    <RecordingDialog
                      dialogProps={recordingDialogContentProps}
                      onClose={closeDialogForRecording}
                    />
                  </div>
                </>
              )}
            {uiStore.toggles.providersVersionsDialog && <SheetProviders />}
            {uiStore.toggles.extendedEpisodeDescriptionDialog.toggle && (
              <SheetDescriptionExtended />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

ProgramSheet.displayName = 'ProgramSheet';

export default ProgramSheet;
